import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    LinearProgress,
    Alert,
    alpha,
    Link,
    TextField,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import useTheme from '@mui/system/useTheme';
import { usePusher } from 'contexts/PusherContext';
import { useSelector } from 'react-redux';
import { userSelector } from 'redux/reducers/user';
import { useAppDispatch } from 'redux/store';
import { pushMessage } from 'redux/reducers/messages';
import {
    scrapeAthleticNetProfile,
    getAthleticNetJobStatus,
    getAthleticNetScrapedData,
} from 'api/AthleticNet';
import type { AthleticNetScrapedDataResponse } from 'api/AthleticNet';
import { Individual } from 'utils/types';
import AthleticNetEventReview from './AthleticNetEventReview';

interface AthleticNetScraperDialogProps {
    open: boolean;
    onClose: () => void;
    individual: Individual;
    onSuccess?: () => void;
}

const AthleticNetScraperDialog: React.FC<AthleticNetScraperDialogProps> = ({
    open,
    onClose,
    individual,
    onSuccess,
}) => {
    const pusher = usePusher();
    const { userData } = useSelector(userSelector);
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const glassStyle = {
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    };

    // Core state
    const [loading, setLoading] = useState(false);
    const [jobId, setJobId] = useState<string | null>(null);
    const [status, setStatus] = useState<string>('');
    const [statusMessage, setStatusMessage] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Review state
    const [showReview, setShowReview] = useState(false);
    const [scrapedData, setScrapedData] = useState<AthleticNetScrapedDataResponse | null>(null);

    // Refs for polling
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const [url, setUrl] = useState(individual.athletic_net_profile_url || '');

    // --- Pusher event handlers ---
    const handleScrapingStarted = useCallback(
        (data: any) => {
            if (data.job_id !== jobId) return;
            setStatus('processing');
            setStatusMessage(data.message || 'Fetching data from Athletic.net...');
        },
        [jobId],
    );

    const handleScrapingComplete = useCallback(
        async (data: any) => {
            if (data.job_id !== jobId) return;
            setStatus('complete');
            setStatusMessage(data.message || 'Scraping complete! Loading results for review...');

            // Request browser notification
            if (Notification.permission === 'granted') {
                new Notification('Athletic.net Import Complete', {
                    body: `Found ${data.results?.event_count || 0} events for ${
                        data.results?.athlete_name || individual.name
                    }.`,
                });
            }

            // Fetch scraped data for review
            try {
                const response = await getAthleticNetScrapedData(data.job_id);
                setScrapedData(response.data);
                setShowReview(true);
                setLoading(false);
            } catch (err) {
                setError('Failed to load scraped data for review.');
                setLoading(false);
            }
        },
        [jobId, individual.name],
    );

    const handleScrapingFailed = useCallback(
        (data: any) => {
            if (data.job_id !== jobId) return;
            setStatus('failed');
            setStatusMessage(data.message || 'Import failed.');
            setError(data.error || 'An unknown error occurred.');
            setLoading(false);

            if (Notification.permission === 'granted') {
                new Notification('Athletic.net Import Failed', {
                    body: data.error || 'An error occurred during import.',
                });
            }
        },
        [jobId],
    );

    // --- Pusher subscription ---
    useEffect(() => {
        if (!pusher || !userData || !jobId) return;

        const channel = pusher.subscribe(`private-users.${userData.uuid}`);

        channel.bind('athletic-net.scraping.started', handleScrapingStarted);
        channel.bind('athletic-net.scraping.complete', handleScrapingComplete);
        channel.bind('athletic-net.scraping.failed', handleScrapingFailed);

        return () => {
            channel.unbind('athletic-net.scraping.started', handleScrapingStarted);
            channel.unbind('athletic-net.scraping.complete', handleScrapingComplete);
            channel.unbind('athletic-net.scraping.failed', handleScrapingFailed);
            pusher.unsubscribe(`private-users.${userData.uuid}`);
        };
    }, [
        pusher,
        userData,
        jobId,
        handleScrapingStarted,
        handleScrapingComplete,
        handleScrapingFailed,
    ]);

    // --- Polling fallback (runs regardless of Pusher, catches sync-queue races) ---
    useEffect(() => {
        if (!jobId || status === 'complete' || status === 'failed' || showReview) {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            return;
        }

        pollingRef.current = setInterval(async () => {
            try {
                const response = await getAthleticNetJobStatus(jobId);
                const data = response.data;

                if (data.status === 'processing') {
                    setStatus('processing');
                    setStatusMessage(data.message || 'Fetching data from Athletic.net...');
                } else if (data.status === 'complete') {
                    setStatus('complete');
                    setStatusMessage('Scraping complete! Loading results for review...');

                    // Fetch scraped data
                    const scrapedResponse = await getAthleticNetScrapedData(jobId);
                    setScrapedData(scrapedResponse.data);
                    setShowReview(true);
                    setLoading(false);
                } else if (data.status === 'failed') {
                    setStatus('failed');
                    setStatusMessage(data.message || 'Import failed.');
                    setError(data.error || 'An unknown error occurred.');
                    setLoading(false);
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
        }, 3000);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [jobId, status, showReview]);

    // --- Request notification permission ---
    useEffect(() => {
        if (open && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, [open]);

    // --- Start import ---
    const handleStartImport = async () => {
        if (!url) {
            setError('No Athletic.net profile URL configured for this athlete.');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setStatus('pending');
            setStatusMessage('Queueing import job...');

            const response = await scrapeAthleticNetProfile({
                url,
                individual_uuid: individual.uuid,
            });

            const { job_id, status: jobStatus } = response.data;
            setJobId(job_id);

            // With sync queue, the job may already be complete by the
            // time the HTTP response arrives. Handle immediately.
            if (jobStatus === 'complete') {
                setStatus('complete');
                setStatusMessage('Scraping complete! Loading results for review...');
                try {
                    const scrapedResponse = await getAthleticNetScrapedData(job_id);
                    setScrapedData(scrapedResponse.data);
                    setShowReview(true);
                    setLoading(false);
                } catch (fetchErr) {
                    setError('Failed to load scraped data for review.');
                    setLoading(false);
                }
            } else if (jobStatus === 'failed') {
                setStatus('failed');
                setStatusMessage(response.data.message || 'Import failed.');
                setError(response.data.error || 'An unknown error occurred.');
                setLoading(false);
            }
            // Otherwise status is 'pending'/'processing' and Pusher/polling will handle it
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to start import.');
            setLoading(false);
            setStatus('');
        }
    };

    // --- Import confirmed callback ---
    const handleImportConfirmed = (importedCount: number) => {
        setShowReview(false);
        setScrapedData(null);

        dispatch(
            pushMessage({
                status: 'success',
                message: `Successfully imported ${importedCount} performance(s) from Athletic.net.`,
            }),
        );

        if (onSuccess) {
            onSuccess();
        }
        onClose();
    };

    // --- Reset on close ---
    const handleClose = () => {
        if (loading && status !== 'complete' && status !== 'failed') {
            // Don't close while actively scraping
            return;
        }
        setLoading(false);
        setJobId(null);
        setStatus('');
        setStatusMessage('');
        setError(null);
        setShowReview(false);
        setScrapedData(null);
        onClose();
    };

    // --- If review is open, show the review component ---
    if (showReview && scrapedData && jobId) {
        return (
            <AthleticNetEventReview
                open={true}
                onClose={() => {
                    setShowReview(false);
                    handleClose();
                }}
                scrapedData={scrapedData}
                jobId={jobId}
                individualUuid={individual.uuid}
                individualName={individual.name}
                onImportConfirmed={handleImportConfirmed}
            />
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: glassStyle }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Import from Athletic.net
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        disabled={loading && status !== 'complete' && status !== 'failed'}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Athlete info */}
                <Box
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        background: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                >
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Athlete
                    </Typography>
                    <Typography variant="h6" fontWeight={500} sx={{ mb: 1.5 }}>
                        {individual.name}
                    </Typography>

                    <TextField
                        label="Athletic.net Profile URL"
                        placeholder="https://www.athletic.net/athlete/12345/track-and-field/"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={loading}
                        helperText={
                            !url ? "Paste the athlete's Athletic.net profile URL above" : undefined
                        }
                        InputProps={{
                            endAdornment: url ? (
                                <Link
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', ml: 0.5 }}
                                >
                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                </Link>
                            ) : undefined,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: alpha(theme.palette.background.paper, 0.6),
                            },
                        }}
                    />
                </Box>

                {/* Progress bar */}
                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* Status alerts */}
                {status === 'pending' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {statusMessage || 'Queueing import job...'}
                    </Alert>
                )}

                {status === 'processing' && (
                    <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
                        {statusMessage || 'Fetching data from Athletic.net...'}
                    </Alert>
                )}

                {status === 'complete' && !showReview && (
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                        {statusMessage || 'Scraping complete! Loading review...'}
                    </Alert>
                )}

                {status === 'failed' && (
                    <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                        {error || 'Import failed. Please try again.'}
                    </Alert>
                )}

                {/* Error without status */}
                {error && !status && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Info text when idle */}
                {!status && !error && (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 2,
                        }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight={800}
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Scrape then Review!
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading && status !== 'complete' && status !== 'failed'}
                >
                    Cancel
                </Button>
                {(!status || status === 'failed') && (
                    <Button
                        variant="contained"
                        onClick={handleStartImport}
                        disabled={loading || !url}
                    >
                        {status === 'failed' ? 'Retry Scraper!' : 'Launch Scraper!'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AthleticNetScraperDialog;
